import {
  ClientWorkFlowStatus,
  FrequencyAndScheduleStatus,
  IClientInvoice,
  IClientInvoiceCreateRequest,
  IClientInvoiceTemplate,
  IClientInvoiceTemplateUpdateRequest,
  IClientInvoiceUpdateRequest,
  ICollaboratorSchema,
  IDeductionResponse,
  IInvoice,
  IInvoiceCollaboratorCreateRequest,
  IInvoiceTemplate,
  InvoiceStatus,
  Intervals,
  IPayableSchema,
  IPayRequest,
  IPayrollSettings,
  IPayrollSettingsUpdate,
  IScheduleDateUpdate,
  MemberWorkFlowStatus,
  PayrollWorkflowStrategy,
  ScheduleStatus,
  WorkFlowSubStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  WSMutationConfig,
  WSMutationsConfig,
  WSQueryCache
} from "@ws-react-query";
import { isEqual } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { InvoicesFormValues } from "../../Invoices/components/InvoicesForm/InvoicesForm";
import { AttachmentValues } from "../../Invoices/components/InvoicesForm/OtherSection";
import {
  PayableFormValues,
  SendPayableInvoiceAction
} from "../../Invoices/components/PayableForm/PayableForm";
import { selectorBillingAttachmentSync } from "../../Invoices/selectors/selectorBillingAttachmentSync";
import { useDownloadPdf } from "../../Invoices/utils";
import {
  attachmentsFormDataToCreateRequest,
  attachmentsFormDataToUpdateRequest,
  convertToLineItemsCreateRequest,
  convertToLineItemsUpdateRequest,
  convertToLineItemsUpdateRequest2,
  convertToLineItemsV3CreateRequest,
  creditFeeFormDataToRequest,
  generateDefaultDueDateSetByPayer,
  generateDueDate,
  generateFrequencyConfig,
  generateLateFeeConfig,
  generateSendDate,
  paymentMethodsFormDataToCreateRequest,
  paymentMethodsFormDataToUpdateRequest,
  useInvoiceFormGoBack
} from "../../Invoices/utils/invoicesFormUtils";
import { paymentsService } from "../../services/payments";
import { convertDateToMidday, isToday } from "../../utils/dates";
import { getChangedData } from "../../utils/getChangedData";
import { useWSMutation } from "../helpers";
import { useUserId } from "../hooks/helpers";
import {
  QUERY_CLIENT_INVOICE,
  QUERY_CLIENT_INVOICE_TEMPLATE,
  QUERY_COLLABORATOR_DEDUCTIONS,
  QUERY_INVOICE,
  QUERY_INVOICE_TEMPLATE,
  QUERY_INVOICE_TEMPLATES,
  QUERY_INVOICES,
  QUERY_PAYABLE,
  QUERY_PAYABLES,
  QUERY_PAYROLL_SETTINGS
} from "./keys";

import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { IDeductionCreateRequest } from "@wingspanhq/payments/dist/interfaces/api/deductions";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { transformErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import {
  CreditFeeConfig,
  InvoiceSplitCreate,
  LateFeeConfig,
  LateFeeConfigFrequencyInterval,
  Invoice,
  Payable,
  PaymentDocumentCurrency,
  UpdatePayableWorkflowStatusRequestPayerApprovalStatus,
  PaymentMethod as PaymentMethodV3
} from "@wingspanhq/wingspan-api-sdk";
import { invoicesApi, payablesApi, payersApi } from "@api";
import {
  queriesInvoices,
  queriesInvoiceRows,
  queriesPayableRows,
  queriesPayables
} from "@query";
import { ErrorContextKey } from "../../services/platform";
import { useDefaultCurrency } from "../../shared/hooks/useDefaultCurrency";
import { WSServiceError } from "../../utils/serviceHelper";
import { QUERY_PAYER_ROWS } from "../search/payer/queries/useQueryPayerRows";

const findCreatedInvoice = async (
  invoiceTemplateId: string
): Promise<IInvoice | undefined> => {
  try {
    return (await paymentsService.invoice.list()).find(
      invoice => invoice.invoiceTemplateId === invoiceTemplateId
    );
  } catch (error) {
    console.error("Failed to fetch invoices: ", error);
  }
};

/**
 * Brings an invoice's v3 attachments in line with what the form submitted
 * (WIN-15134). v3 has no bulk attachment write, so the reconciliation is a set
 * of per-file bind/detach calls against what is currently bound.
 *
 * A failure propagates: the caller's save has already landed, so surfacing the
 * error is better than silently dropping a file the payee attached.
 *
 * UNUSED while attachments are disabled — the bind verbs work, but nothing can
 * mint the `memberFileId` they need (see the attachments block in OtherSection
 * for the full reasoning). Kept so re-enabling is just restoring the two call
 * sites below.
 */

const syncInvoiceAttachments = async (
  invoiceId: string,
  formAttachments: AttachmentValues[]
) => {
  // An empty field means nothing was seeded and nothing added, so there is
  // nothing to reconcile — skip the read rather than probing on every save.
  if (formAttachments.length === 0) {
    return;
  }

  const desiredMemberFileIds =
    attachmentsFormDataToCreateRequest(formAttachments);
  const current = await invoicesApi.listInvoiceAttachments({ invoiceId });

  const { attachMemberFileIds, detachAttachmentIds } =
    selectorBillingAttachmentSync({
      current: current.data ?? [],
      desiredMemberFileIds
    });

  for (const attachmentId of detachAttachmentIds) {
    await invoicesApi.detachInvoiceDocument({ invoiceId, attachmentId });
  }

  for (const memberFileId of attachMemberFileIds) {
    await invoicesApi.attachInvoiceDocument({
      invoiceId,
      billingAttachmentCreate: { memberFileId }
    });
  }
};

export const useMutationSendInvoiceTemplate = () => {
  const history = useHistory();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        status: FrequencyAndScheduleStatus.Active
      });

      const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

      if (createdInvoice) {
        history.push(
          `/member/invoices/${createdInvoice.invoiceId}/send-success`
        );
      } else {
        history.push(
          `/member/invoices/template/${invoiceTemplateId}/send-success`
        );
      }
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useMutationSendNowInvoiceTemplate = () => {
  const history = useHistory();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        scheduleDates: [{ date: new Date(), status: ScheduleStatus.Pending }],
        status: FrequencyAndScheduleStatus.Active
      });

      const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

      if (createdInvoice) {
        history.push(
          `/member/invoices/${createdInvoice.invoiceId}/send-success`
        );
      } else {
        history.push(
          `/member/invoices/template/${invoiceTemplateId}/send-success`
        );
      }
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useMutationAcceptInvoice = () => {
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      await paymentsService.invoice.update(invoiceId, {
        member: {
          workflowStatus: MemberWorkFlowStatus.Accepted
        }
      });
    },
    {
      onSuccess: (_, { invoiceId }) => {
        openSnackbar({
          type: "success",
          message: "Invoice accepted"
        });

        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
      }
    }
  );
};

export const useMutationRetryInvoicePayment = () => {
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      await paymentsService.invoice.pay(invoiceId);
    },
    {
      onSuccess: (_, { invoiceId }) => {
        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
      },
      onError: () => {
        openSnackbar({ type: "warning", message: "Failed to retry payment" });
      }
    }
  );
};

export const useMutationCancelInvoiceTemplate = () => {
  return useWSMutation<
    IInvoiceTemplate,
    WSServiceError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      return await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        status: FrequencyAndScheduleStatus.Expired,
        frequency: {
          endDate: new Date()
        }
      });
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useMutationDeleteInvoiceTemplate = () => {
  return useWSMutation<
    IInvoiceTemplate,
    WSServiceError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      return await paymentsService.invoiceTemplate.delete(invoiceTemplateId);
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useMutationSaveInvoice = () => {
  const userId = useUserId();
  const goBack = useInvoiceFormGoBack();
  const history = useHistory();
  const location = useLocation();

  return useWSMutation<
    {
      savedInvoice?: Invoice;
      savedInvoiceId?: string;
      savedInvoiceTemplate?: IInvoiceTemplate;
    },
    WSServiceError,
    {
      invoiceId?: string;
      invoiceTemplateId?: string;
      data: InvoicesFormValues & {
        dueInDays?: number;
      };
      send?: boolean;
      resubmit?: boolean;
      currency: Currency;
    }
  >(
    async ({
      invoiceId,
      invoiceTemplateId,
      data,
      send,
      resubmit,
      currency
    }) => {
      let invoice: IInvoice | undefined;
      let invoiceTemplate: IInvoiceTemplate | undefined;
      let savedInvoice: Invoice | undefined;
      let savedInvoiceId: string | undefined;
      let savedInvoiceTemplate: IInvoiceTemplate | undefined;

      if (invoiceTemplateId) {
        invoiceTemplate =
          await paymentsService.invoiceTemplate.get(invoiceTemplateId);
      }

      // Payer
      const payerId = data.payer?.payerId as string;
      const payerEngagementId = data.payer?.payerEngagementId as string;

      // The picker writes v3 ids, so the CC sync resolves end-to-end
      // (`Payer.notificationCcEmails` / `PayerUpdate.notificationCcEmails`,
      // WIN-15229). It stays wrapped in a catch: a CC sync failure should not
      // fail the invoice save.
      try {
        const payer = await payersApi.getPayer({ payerId });

        const additionalEmails = (data.payer?.emailsCC || [])
          .map(i => i.email)
          .filter(e => !!e);
        if (!isEqual(additionalEmails, payer.notificationCcEmails || [])) {
          // `[]` explicitly clears the stored list (WIN-15229 semantics); omitting the
          // field would preserve it.
          await payersApi.updatePayer({
            payerId,
            payerUpdate: { notificationCcEmails: additionalEmails }
          });
        }
      } catch (error) {
        console.error("Failed to update client email CCs", error);
      }

      if (!payerEngagementId) {
        throw new Error("No payerEngagementId set");
      }

      // The v1 `collaborators` payload for the invoiceTemplate branches below.
      // v1 identified a split by the engagement alone (`memberClientId`), where
      // v3 takes `payeeId` + `payeeEngagementId` — see the `splits` mapping on
      // the single-invoice path.
      const collaboratorsRequest: (IInvoiceCollaboratorCreateRequest | null)[] =
        data.splits.map(split => {
          if (
            split.remove ||
            !split.payee?.payeeEngagementId ||
            !split.amount
          ) {
            return null;
          }

          return {
            memberClientId: split.payee.payeeEngagementId,
            amount: split.amount,
            currency,
            description: split.description
          };
        });

      const collaboratorsCreateRequestData = collaboratorsRequest.filter(
        collaborator => !!collaborator
      ) as IInvoiceCollaboratorCreateRequest[];

      const sendDate = generateSendDate({
        send: data.send
      });

      let dueDate: Date;
      if (data.dueInDays !== undefined) {
        // if dueInDays is set by payer then generate due date based on it
        dueDate = generateDefaultDueDateSetByPayer({
          dueInDays: data.dueInDays,
          sendDate
        });
      } else {
        // Generate due date based on form values and send date
        dueDate = generateDueDate({
          formValues: {
            due: data.due,
            customDueDate: data.customDueDate
          },
          sendDate
        });
      }

      // Generate object with common invoice data which can be used for both invoice and invoiceTemplate
      const commonData = {
        memberId: userId,
        currency,
        // v1 payload field names: the engagement is `memberClientId` and the
        // payer is `clientId` on the invoiceTemplate contract.
        memberClientId: payerEngagementId,
        clientId: payerId,
        invoiceNotes: data.other.notes,
        creditFeeHandling: creditFeeFormDataToRequest(
          data.advanced.creditFeeHandling
        ),
        notificationPreferences: {
          sendInvoice: data.advanced.sendEmails,
          sendReminders:
            data.advanced.sendEmails && !!data.advanced.sendReminders
        },
        dueDate,
        lateFeeHandling: data.includesLateFee
          ? generateLateFeeConfig({
              formValues: data.lateFee,
              dueDate
            })
          : undefined,
        acceptedPaymentMethods: paymentMethodsFormDataToCreateRequest(
          data.paymentMethods
        ),
        labels: {
          projectName: data.other.projectName || (undefined as any as string),
          subject: data.subject
        },
        metadata: {
          invoiceDate: convertDateToMidday(data.advanced.date),
          purchaseOrderNumber: data.purchaseOrderNumber || undefined
        }
      };

      // v3 field construction, shared by the create and edit paths below.
      const acceptedPaymentMethodsV3: PaymentMethodV3[] = data.paymentMethods
        .acceptAllPMs
        ? [PaymentMethodV3.CREDIT, PaymentMethodV3.ACH, PaymentMethodV3.MANUAL]
        : [
            ...(data.paymentMethods.acceptCreditCard
              ? [PaymentMethodV3.CREDIT]
              : []),
            ...(data.paymentMethods.acceptACH ? [PaymentMethodV3.ACH] : []),
            ...(data.paymentMethods.acceptManual
              ? [PaymentMethodV3.MANUAL]
              : [])
          ];

      const creditFeeHandlingV3: CreditFeeConfig = data.advanced
        .creditFeeHandling?.clientPaysProcessingFees
        ? {
            payerFlatFeePercentage:
              data.advanced.creditFeeHandling.clientProcessingFeePercentage
          }
        : { payeeFeePercentage: 100 };

      let lateFeeHandlingV3: LateFeeConfig | undefined;
      if (data.includesLateFee) {
        lateFeeHandlingV3 = data.lateFee.overriden
          ? {
              lateFeeAmount: data.lateFee.compoundedAmount,
              frequency: {
                interval:
                  LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_WEEKLY,
                every: 1
              }
            }
          : {
              ...(data.lateFee.type === "percent"
                ? { lateFeePercentage: data.lateFee.amount }
                : {}),
              ...(data.lateFee.type === "fixed"
                ? { lateFeeAmount: data.lateFee.amount }
                : {}),
              frequency: {
                interval:
                  data.lateFee.interval === Intervals.Month
                    ? LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_MONTHLY
                    : LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_WEEKLY,
                every: data.lateFee.every
              }
            };
      }

      const notificationPreferencesV3 = {
        shouldSendInvoice: data.advanced.sendEmails,
        shouldSendReminders:
          data.advanced.sendEmails && !!data.advanced.sendReminders
      };

      const metadataV3 = {
        subject: data.subject,
        ...(data.other.projectName
          ? { projectName: data.other.projectName }
          : {}),
        invoiceDate: convertDateToMidday(data.advanced.date).toISOString(),
        ...(data.purchaseOrderNumber
          ? { purchaseOrderNumber: data.purchaseOrderNumber }
          : {})
      };

      if (
        !data.recurring &&
        (data.send.type === "immediately" || isToday(sendDate))
      ) {
        // If invoice is not repeats and have to be sent immediately – we can work just with invoice entity

        if (invoiceId) {
          // v3 `InvoiceUpdate` carries no client/engagement, currency, splits or
          // member workflow status: re-pointing an invoice at a different client,
          // editing its contractor splits, and the dispute-resubmit flip are all
          // outside the update contract. The invoice's own fields below are what
          // an edit can change.
          savedInvoice = await invoicesApi.updateInvoice({
            invoiceId,
            invoiceUpdate: {
              lineItems: convertToLineItemsV3CreateRequest(data.lineItems),
              dueDate,
              notes: data.other.notes,
              acceptedPaymentMethods: acceptedPaymentMethodsV3,
              creditFeeHandling: creditFeeHandlingV3,
              lateFeeHandling: lateFeeHandlingV3,
              notificationPreferences: notificationPreferencesV3,
              metadata: metadataV3
            }
          });
          savedInvoiceId = savedInvoice.id;

          // Attachments no longer ride along on the update body — they are
          // reconciled through the v3 per-file bind/detach verbs (WIN-15134).
          // Disabled while there is no way to upload a file (see OtherSection).
          // await syncInvoiceAttachments(invoiceId, data.other.attachments);
        } else if (invoiceTemplateId) {
          // This is the case when member have one-time scheduled for future invoice but wants to send it now, so they updates send date to today in Edit form. In this case we're just updating existing invoiceTemplate and it will create new Open invoice.

          savedInvoiceTemplate = await paymentsService.invoiceTemplate.update(
            invoiceTemplateId,
            getChangedData(
              invoiceTemplate,
              {
                invoiceData: {
                  ...commonData,
                  collaborators: collaboratorsRequest,
                  lineItems: convertToLineItemsUpdateRequest(data.lineItems),
                  acceptedPaymentMethods: paymentMethodsFormDataToUpdateRequest(
                    data.paymentMethods
                  ),
                  attachments: {
                    customAttachmentIds: attachmentsFormDataToUpdateRequest(
                      data.other.attachments,
                      invoiceTemplate?.invoiceData.attachments
                        ?.customAttachmentIds || []
                    )
                  }
                },
                scheduleDates: [
                  {
                    date: sendDate
                  }
                ],
                labels: commonData.labels
              },
              { dismissTimeInDates: true }
            )
          );
        } else {
          const splits: InvoiceSplitCreate[] = data.splits.flatMap(split => {
            if (
              split.remove ||
              !split.payee?.payeeId ||
              !split.payee?.payeeEngagementId ||
              !split.amount
            ) {
              return [];
            }

            return [
              {
                payeeId: split.payee.payeeId,
                payeeEngagementId: split.payee.payeeEngagementId,
                amount: split.amount,
                currency,
                description: split.description
              }
            ];
          });

          const createdInvoice = await invoicesApi.createInvoice({
            invoiceCreate: {
              payerId,
              payerEngagementId,
              currency: currency as unknown as PaymentDocumentCurrency,
              dueDate,
              lineItems: convertToLineItemsV3CreateRequest(data.lineItems),
              splits,
              notes: data.other.notes,
              acceptedPaymentMethods: acceptedPaymentMethodsV3,
              creditFeeHandling: creditFeeHandlingV3,
              lateFeeHandling: lateFeeHandlingV3,
              notificationPreferences: notificationPreferencesV3,
              metadata: metadataV3
            }
          });
          savedInvoiceId = createdInvoice.id;

          // v3 `InvoiceCreate` carries no attachments, so the files the form
          // uploaded are bound afterwards. This runs before the optional send
          // below so a sent invoice already carries them.
          // Disabled while there is no way to upload a file (see OtherSection).
          // if (savedInvoiceId) {
          //   await syncInvoiceAttachments(
          //     savedInvoiceId,
          //     data.other.attachments
          //   );
          // }
        }
      } else {
        // If invoice repeats OR sending should be delayed – we are working with invoiceTemplate entity

        // Generate frequency config if invoice have to be repeated
        const frequency = data.recurring
          ? generateFrequencyConfig({
              formValues: data.frequency,
              sendDate
            })
          : undefined;

        if (invoiceTemplateId) {
          let scheduleDates: (IScheduleDateUpdate | null)[] | undefined;

          if (invoiceTemplate?.isSchedulingOnly) {
            // New scheduling logic

            if (frequency) {
              // If invoice is repeatable

              if (frequency.endDate) {
                // If repeatable invoice has end date – we need to cut off everything after end date

                scheduleDates = invoiceTemplate?.scheduleDates?.map(
                  scheduleDate =>
                    frequency.endDate && scheduleDate.date > frequency.endDate
                      ? null
                      : {}
                );
              }

              // If repeatable invoice is endless don't do anything
            } else {
              // If it's one time scheduled invoice, just update send date

              scheduleDates = [
                {
                  date: sendDate
                }
              ];
            }
          } else {
            // Old scheduling logic

            scheduleDates = [
              {
                date: sendDate
              }
            ];
          }

          // If invoiceTemplate is being edited – update invoiceTemplate
          savedInvoiceTemplate = await paymentsService.invoiceTemplate.update(
            invoiceTemplateId,
            getChangedData(
              invoiceTemplate,
              {
                invoiceData: {
                  ...commonData,
                  collaborators: collaboratorsRequest,
                  lineItems: convertToLineItemsUpdateRequest2(
                    invoiceTemplate?.invoiceData.lineItems || [],
                    data.lineItems
                  ),
                  lateFeeHandling:
                    invoiceTemplate?.invoiceData.lateFeeHandling &&
                    !commonData.lateFeeHandling
                      ? {
                          lateFeePercentage: null,
                          lateFeeAmount: null,
                          frequency: null
                        }
                      : commonData.lateFeeHandling,
                  acceptedPaymentMethods: paymentMethodsFormDataToUpdateRequest(
                    data.paymentMethods
                  ),
                  attachments: {
                    customAttachmentIds: attachmentsFormDataToUpdateRequest(
                      data.other.attachments,
                      invoiceTemplate?.invoiceData.attachments
                        ?.customAttachmentIds || []
                    )
                  }
                },
                frequency: frequency
                  ? frequency
                  : invoiceTemplate?.frequency
                    ? null
                    : undefined,
                scheduleDates,
                labels: commonData.labels
              },
              { dismissTimeInDates: true }
            )
          );
        } else {
          if (invoiceId) {
            // Only this branch (turning an invoice into a recurring/scheduled
            // template) needs the existing invoice, so the read stays local to
            // it — the plain edit path above must not pay for a v1 fetch.
            invoice = await paymentsService.invoice.get(invoiceId);

            if (invoice?.status === InvoiceStatus.Draft) {
              await paymentsService.invoice.delete(invoiceId);
            }
          }

          // Create new invoiceTemplate
          savedInvoiceTemplate = await paymentsService.invoiceTemplate.create({
            invoiceData: {
              ...commonData,
              collaborators: collaboratorsCreateRequestData,
              lineItems: convertToLineItemsCreateRequest(data.lineItems),
              // Invoice templates still write attachments on v1 — only the
              // single-invoice paths bind through v3.
              attachments: {
                customAttachmentIds: attachmentsFormDataToCreateRequest(
                  data.other.attachments
                )
              }
            },
            frequency,
            scheduleDates: [{ date: sendDate, status: ScheduleStatus.Pending }],
            status: FrequencyAndScheduleStatus.Draft,
            labels: commonData.labels,
            isSchedulingOnly: true
          });
        }
      }

      if (send) {
        if (savedInvoiceId) {
          await invoicesApi.sendInvoice({ invoiceId: savedInvoiceId });

          history.push(
            `/member/invoices/${savedInvoiceId}/send-success`,
            location.state
          );
        }

        if (savedInvoiceTemplate) {
          savedInvoiceTemplate = await paymentsService.invoiceTemplate.update(
            savedInvoiceTemplate.invoiceTemplateId,
            {
              status: FrequencyAndScheduleStatus.Active
            }
          );

          const createdInvoice = await findCreatedInvoice(
            savedInvoiceTemplate.invoiceTemplateId
          );

          if (createdInvoice) {
            history.push(
              `/member/invoices/${createdInvoice.invoiceId}/send-success`,
              location.state
            );
          } else {
            history.push(
              `/member/invoices/template/${savedInvoiceTemplate.invoiceTemplateId}/send-success`,
              location.state
            );
          }
        }
      }

      return {
        savedInvoice,
        savedInvoiceId,
        savedInvoiceTemplate
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ savedInvoiceId, savedInvoiceTemplate }, { send }) => {
        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(QUERY_INVOICE_TEMPLATES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries(QUERY_PAYER_ROWS);
        WSQueryCache.invalidateQueries(queriesInvoices.getInvoice.queryKeyBase);
        WSQueryCache.invalidateQueries(
          queriesInvoices.listInvoices.queryKeyBase
        );
        WSQueryCache.invalidateQueries(
          queriesInvoices.searchInvoices.queryKeyBase
        );

        if (savedInvoiceId) {
          WSQueryCache.invalidateQueries([QUERY_INVOICE, savedInvoiceId]);
        }
        if (savedInvoiceTemplate) {
          WSQueryCache.invalidateQueries([
            QUERY_INVOICE_TEMPLATE,
            savedInvoiceTemplate.invoiceTemplateId
          ]);
        }

        if (!send) {
          goBack();
        }
      }
    }
  );
};

export const useMutationSendInvoice = () => {
  const history = useHistory();
  const location = useLocation();
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    {
      invoiceTemplate?: IInvoiceTemplate;
    },
    WSServiceError,
    {
      invoiceId?: string;
      invoiceTemplateId?: string;
    }
  >(
    async ({ invoiceId, invoiceTemplateId }) => {
      let invoiceTemplate: IInvoiceTemplate | undefined;

      if (invoiceId) {
        // Sending a single invoice is a clean v3 op (draft → sent); the
        // recurring-template activation below stays on v1.
        await invoicesApi.sendInvoice({ invoiceId });

        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
        WSQueryCache.invalidateQueries(
          queriesInvoices.getInvoice.getQueryKey({ invoiceId })
        );

        history.push(
          `/member/invoices/${invoiceId}/send-success`,
          location.state
        );
      }

      if (invoiceTemplateId) {
        invoiceTemplate = await paymentsService.invoiceTemplate.update(
          invoiceTemplateId,
          {
            status: FrequencyAndScheduleStatus.Active
          }
        );

        const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

        if (createdInvoice) {
          history.push(
            `/member/invoices/${createdInvoice.invoiceId}/send-success`,
            location.state
          );
        } else {
          history.push(
            `/member/invoices/template/${invoiceTemplateId}/send-success`,
            location.state
          );
        }
      }

      return {
        invoiceTemplate
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ invoiceTemplate }) => {
        openSnackbar({
          type: "success",
          message: "Invoice has been sent"
        });

        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );

        if (invoiceTemplate) {
          WSQueryCache.invalidateQueries([
            QUERY_INVOICE_TEMPLATE,
            invoiceTemplate.invoiceTemplateId
          ]);
          WSQueryCache.invalidateQueries(QUERY_INVOICE_TEMPLATES);
        }
      },
      onError: error => {
        openSnackbar({
          type: "warning",
          message: transformErrorMessage({
            error,
            contextKey: ErrorContextKey.InvoiceForm
          })
        });
      }
    }
  );
};

export const useMutationCreateClientInvoice = () => {
  return useWSMutation<
    IClientInvoice,
    WSServiceError,
    IClientInvoiceCreateRequest
  >(data => paymentsService.client.invoice.create(data), {
    throwOnError: false,
    dependencies: [QUERY_CLIENT_INVOICE]
  });
};

export const useMutationUpdateClientInvoice = (invoiceId: string) => {
  return useWSMutation<
    IClientInvoice,
    WSServiceError,
    IClientInvoiceUpdateRequest
  >(data => paymentsService.client.invoice.update(invoiceId, data), {
    throwOnError: false,
    dependencies: [[QUERY_CLIENT_INVOICE, invoiceId]]
  });
};

export const useMutationPayClientInvoice = (
  invoiceId: string,
  config?: WSMutationsConfig<IClientInvoice, WSServiceError, IPayRequest>
) => {
  return useWSMutation<IClientInvoice, WSServiceError, IPayRequest>(
    data => paymentsService.client.invoice.pay(invoiceId, data),
    {
      throwOnError: false,
      dependencies: [[QUERY_CLIENT_INVOICE, invoiceId]],
      ...config
    }
  );
};

export const useMutationUpdateClientInvoiceTemplate = () => {
  return useWSMutation<
    IClientInvoiceTemplate,
    WSServiceError,
    { invoiceTemplateId: string; data: IClientInvoiceTemplateUpdateRequest }
  >(
    ({ invoiceTemplateId, data }) =>
      paymentsService.client.invoiceTemplate.update(invoiceTemplateId, data),
    {
      throwOnError: false,
      dependencies: [QUERY_CLIENT_INVOICE_TEMPLATE]
    }
  );
};

export const useMutationSavePayable = () => {
  const defaultCurrency = useDefaultCurrency();

  return useWSMutation<
    {
      savedPayable: Payable;
    },
    WSServiceError,
    {
      payableId?: string;
      data: PayableFormValues;
      resubmit?: boolean;
    }
  >(
    async ({ data, payableId }) => {
      // v3 `PayableUpdate` carries only line items, dates, notes and metadata —
      // the v1 body's currency, credit-fee split and workflow sub-status have no
      // update-side twin, so an edit no longer rewrites them. `resubmit` (the v1
      // dispute-resubmit workflow flip) likewise has no v3 field; the dispute
      // resolution itself runs through the workflow-status verb.
      const metadata = {
        ...(data.other.projectName
          ? { projectName: data.other.projectName }
          : {}),
        ...(data.purchaseOrderNumber
          ? { purchaseOrderNumber: data.purchaseOrderNumber }
          : {})
      };

      const lineItems =
        data.lineItems && data.lineItems.length > 0
          ? convertToLineItemsV3CreateRequest(data.lineItems)
          : [{ description: "", totalCost: 0 }];

      let savedPayable: Payable;

      if (payableId) {
        savedPayable = await payablesApi.updatePayable({
          payableId,
          ifMatch: "*",
          payableUpdate: {
            lineItems,
            dueDate: data.payDate,
            notes: data.other.notes,
            metadata
          }
        });
      } else {
        if (!data.payee) throw new Error("No payee set");

        savedPayable = await payablesApi.createPayable({
          payableCreate: {
            // `PayableCreate` is an XOR: exactly one of `payeeId` /
            // `payeeEngagementId`. The engagement is the more specific of the
            // two (and implies the payee), and the form always carries it.
            payeeEngagementId: data.payee.payeeEngagementId,
            currency: defaultCurrency as unknown as PaymentDocumentCurrency,
            lineItems,
            dueDate: data.payDate,
            notes: data.other.notes,
            metadata
          }
        });
      }

      return {
        savedPayable
      };
    },
    {
      throwOnError: false,
      dependencies: [QUERY_PAYABLES]
    }
  );
};

export const useMutationSendPayable = () => {
  return useWSMutation<
    {
      payable?: Payable;
    },
    WSServiceError,
    {
      action: SendPayableInvoiceAction;
      payableId: string;
    }
  >(
    async ({ action, payableId }) => {
      // v1 flipped status (and, when approving, the client workflow status) in a
      // single PATCH. v3 splits those: `open` moves a draft out of Created, and
      // the approval is its own workflow-status verb.
      let payable = await payablesApi.openPayable({ payableId, ifMatch: "*" });

      if (action === "approve") {
        payable = await payablesApi.updatePayableWorkflowStatus({
          payableId,
          updatePayableWorkflowStatusRequest: {
            payerApprovalStatus:
              UpdatePayableWorkflowStatusRequestPayerApprovalStatus.APPROVED
          }
        });
      }

      return {
        payable
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ payable }) => {
        WSQueryCache.invalidateQueries(QUERY_PAYABLES);
        WSQueryCache.invalidateQueries(
          queriesPayableRows.searchPayableRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries(queriesPayables.getPayable.queryKeyBase);
        if (payable?.id) {
          WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.id]);
        }
      }
    }
  );
};

export const useMutationUpdatePayrollSettings = (
  memberId: string,
  config?: WSMutationConfig<IPayrollSettings, WSServiceError>
) => {
  return useWSMutation<
    IPayrollSettings,
    WSServiceError,
    IPayrollSettingsUpdate
  >(
    async data => {
      await paymentsService.service.get();
      return paymentsService.payrollSettings.update(memberId, data);
    },
    {
      dependencies: [QUERY_PAYROLL_SETTINGS],
      ...config
    }
  );
};

export const useMutationDownloadInvoicePdf = () => {
  const downloadPdf = useDownloadPdf();

  return useWSMutation<
    void,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(async ({ invoiceId }) => {
    const invoice = await paymentsService.invoice.generatePdf(invoiceId);

    if (!invoice.attachments?.invoicePdf) {
      throw new Error("No invoice pdf to download");
    }

    downloadPdf(invoice.attachments.invoicePdf);
  });
};

export const useMutationDownloadInvoiceReceiptPdf = () => {
  const downloadPdf = useDownloadPdf();

  return useWSMutation<
    void,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(async ({ invoiceId }) => {
    const invoice = await paymentsService.invoice.generatePdf(invoiceId);

    if (!invoice.attachments?.receiptPdf) {
      throw new Error("No invoice receipt to download");
    }

    downloadPdf(invoice.attachments.receiptPdf);
  });
};

export const useMutationCreateCollaboratorDeduction = () =>
  useWSMutation(
    (data: IDeductionCreateRequest) =>
      paymentsService.collaboratorDeductions.create(data),
    {
      dependencies: [QUERY_COLLABORATOR_DEDUCTIONS]
    }
  );

export const useMutationDeleteCollaboratorDeduction = (
  config?: WSMutationConfig<IDeductionResponse, WSServiceError>
) =>
  useWSMutation<IDeductionResponse, WSServiceError>(
    (id: string) => paymentsService.collaboratorDeductions.delete(id),
    {
      dependencies: [QUERY_COLLABORATOR_DEDUCTIONS],
      ...config
    }
  );
